import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "@/lib/icons";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { RunRecording } from "./-components/run-recording";
import {
  durationLabel,
  modeLabel,
  outcomeLabel,
  outcomeTone,
  runEvents,
  runTurns,
  transferLabel,
  whenLabel,
} from "./-components/run-summary";
import { SavedTimeline } from "./-components/saved-timeline";
import { SavedTranscript } from "./-components/saved-transcript";

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/agent-test/$runId")({
  component: TestRun,
});

/** One test: what was said, what the agent did about it, and the audio when
 *  there is any. */
function TestRun() {
  const { runId } = Route.useParams();
  const run = useQuery(orpc.frontDesk.agent.test.run.queryOptions({ input: { runId } }));

  if (run.isPending) return <Skeleton className="h-64 w-full" />;
  if (run.isError) {
    return (
      <ReadFailed
        message="Could not load this test."
        error={run.error}
        onRetry={() => void run.refetch()}
        retrying={run.isFetching}
      />
    );
  }

  const detail = run.data;
  const transfer = transferLabel(detail.transfer);

  return (
    <>
      <PageHeader
        title="Agent test"
        meta={`${whenLabel(detail.startedAt)}, ${modeLabel(detail.mode, detail.channel)}, ${durationLabel(detail.durationSeconds)}`}
        actions={
          <Button variant="outline" asChild>
            <Link to="/sarah/agent-test">
              <ChevronLeft className="size-4" aria-hidden /> All tests
            </Link>
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={outcomeTone(detail.outcome)}>{outcomeLabel(detail.outcome)}</Badge>
        {transfer ? (
          <span className="text-muted-foreground text-sm">Would transfer to {transfer}</span>
        ) : null}
      </div>

      {/* Only where the audio exists. `hasRecording` is false for every run the bridge has filed so
          far, because it does not upload a WAV yet, so the player is absent rather than offering a
          button that answers nothing. */}
      {detail.hasRecording ? (
        <Card>
          <CardContent>
            <RunRecording runId={detail.id} />
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardContent className="space-y-3">
            <p className="text-sm font-medium">Transcript</p>
            <SavedTranscript turns={runTurns(detail.transcript)} />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3">
            <p className="text-sm font-medium">What it did</p>
            <SavedTimeline events={runEvents(detail.events)} />
          </CardContent>
        </Card>
      </div>
    </>
  );
}
